/**
 * Advance Rate Screen
 *
 * Displays per-supplier advance rate calculations: expected dilution,
 * stress factor, volatility, horizon ratio, dilution reserve, computed
 * advance rate, proposed advance rate, NERB, and available funding.
 *
 * On mount it fetches advance data then criteria data. The criteria
 * thresholds are used to determine each supplier's eligibility status,
 * which is attached as a `classify` function to the "Available Funding"
 * column so DataTable can color-code eligible vs. ineligible suppliers.
 */
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
// -------------------------------------------------------------
import { getAdvanceData } from "Redux/advance/actions";
import { getLinearParameters, calculateAny } from "Redux/data/actions";
import { getCriteriaData } from "Redux/criteria/actions";
import DataTable from "Components/DataTable";
// -------------------------------------------------------------

// -------------------------------------------------------------
// -------------------------------------------------------------
const Advance = () => {
  const dispatch = useDispatch();
  const { advance } = useSelector((state) => state.advance);
  const { criteria } = useSelector((state) => state.criteria);
  const project = useSelector((state) => state.project);
  const [, setCalculating] = useState(false);
  const [headers, setHeaders] = useState([
    { text: "Supplier", key: "Supplier", datatype: "string" },
    {
      text: "Expected Dilution",
      key: "Expected Dilution",
      datatype: "float",
      display: "percentage",
    },
    { text: "Stress", key: "Stress", datatype: "float" },
    {
      text: "Additive Volatility Component",
      key: "Additive Volatility Component",
      datatype: "float",
      display: "percentage",
    },
    { text: "Horizon Ratio", key: "Horizon Ratio", datatype: "float" },
    {
      text: "Dilution Reserve",
      key: "Dilution Reserve",
      datatype: "float",
      display: "percentage",
    },
    {
      text: "Advance Rate",
      key: "Advance Rate",
      datatype: "float",
      display: "percentage",
    },
    {
      text: "Proposed Advance Rate",
      key: "Proposed Advance Rate",
      datatype: "float",
      display: "percentage",
    },
    {
      text: "NERB",
      key: "NERB",
      datatype: "float",
    },

    { text: "Available Funding", key: "Available Funding", datatype: "float", isRounded: true },
  ]);

  useEffect(() => {
    dispatch(
      getAdvanceData(() => {
        dispatch(getCriteriaData(() => {}));
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (project && criteria) {
      const linearParameters = getLinearParameters(project.data);
      const thresholds = {
        "Days Trading With Buyer": parseInt(
          linearParameters.min_days_trading_with_buyer.value
        ),
        "Invoices Last n Days": parseInt(
          linearParameters.min_number_of_invoices.value
        ),
        "Invoicing Months In Year": parseInt(
          linearParameters.min_invoicing_months_in_period.value
        ),
        "Collections In Year": parseInt(
          linearParameters.collections_in_period.value
        ),
        "Supplier Delinquency Ratio": parseFloat(
          linearParameters.delinquency_threshold.value
        ),
        "Supplier Dilution Ratio": parseFloat(
          linearParameters.dilution_threshold.value
        ),
        DSO: parseFloat(linearParameters.dso.value),
      };
      const thisSupplierStatus = {};
      criteria.data.forEach((item) => {
        thisSupplierStatus[item.Supplier] =
          item["Days trading with Buyer"] >=
            thresholds["Days Trading With Buyer"] &&
          item["Invoices Last 90 Days"] >= thresholds["Invoices Last n Days"] &&
          item["Invoicing Months In Year"] >=
            thresholds["Invoicing Months In Year"] &&
          item["Collections In Year"] >= thresholds["Collections In Year"] &&
          item["Supplier Delinquency Ratio"] <=
            thresholds["Supplier Delinquency Ratio"] / 100 &&
          item["Supplier Dilution Ratio"] <=
            thresholds["Supplier Dilution Ratio"] / 100 &&
          item["Supplier DSO"] <= thresholds["DSO"];
      });
      const classify = (thresholds, row) => thisSupplierStatus[row["Supplier"]];
      setHeaders(
        headers.map((header) => {
          if (header.key !== "Available Funding") return header;
          return {
            ...header,
            classify,
          };
        })
      );
    }
  }, [project, criteria, headers]);

  return (
    <div>
      <div
        style={{display: "none"}}
        className="button"
        onClick={() => {
          setCalculating(true);
          dispatch(calculateAny("advance", () => setCalculating(false)));
        }}
      >
        Calculate
      </div>
      {advance && (
        <DataTable data={advance.data} headers={headers} reference="Supplier" />
      )}
    </div>
  );
};

export default Advance;
